// Geschätzte Kosten der Schritte (CLAUDE.md „Kosten immer anzeigen“). Jeder Schritt nennt sie vor dem Start,
// bei Wiederholungen in der Rückfrage. Die Zahlen stammen aus den Live-Läufen (docs/decisions/0011, 0012, 0013).
import { formatCount, formatEuro } from "./format";

/** Schritt 1 (ahrefs): Verbrauch im Abo in Units, keine Euro-Kosten. Mit Top-20-Verlauf gemessen auf ca. 2.800. */
export const KEYWORDS_UNITS = 2800;
/** Eine Gemini-Generierung (Schritt 2, Testlauf der Vorlage): 1.800 bis 3.300 Tokens gemessen. */
export const GEMINI_TOKENS = 3000;
/** Schritt 3: Euro je Lauf (ein Task je System), das Backend rechnet die Schätzung für den Briefing-Schritt genau aus. */
export const FANOUT_RUN_EUR = 0.0356;
/** Euro je Lauf und System (DataForSEO: ChatGPT mit Priorität, Gemini live), gleich den Preisen im Backend. */
export const PRICE_CHATGPT_EUR = 0.0022;
export const PRICE_GEMINI_EUR = 0.0334;

/** Je weiteres Keyword: Terms match, Questions und Also rank for (3 Abfragen mit je ca. 50 Units plus Zeilen). */
export const KEYWORDS_UNITS_PER_EXTRA = 300;
export function keywordsCostFor(extra: number): string {
  return `ca. ${formatCount(KEYWORDS_UNITS + extra * KEYWORDS_UNITS_PER_EXTRA)} ahrefs-API-Units (im Abo, keine Euro-Kosten)`;
}
export const keywordsCost = keywordsCostFor(0);
export const keywordsCache = "Innerhalb von 7 Tagen kostet ein erneuter Lauf mit denselben Eingaben nichts (Cache).";
export const geminiCost = `ca. ${formatCount(GEMINI_TOKENS)} Tokens Gemini (Cent-Bereich)`;

/** Schritt 3 mit der vom Backend geschätzten Summe, z. B. „ca. 0,21 € (DataForSEO: …)“. */
export function fanoutsCost(eur: number): string {
  return `ca. ${formatEuro(eur)} (DataForSEO: ChatGPT mit Priorität, Gemini live)`;
}

export const fanoutRunCost = `ca. ${formatEuro(FANOUT_RUN_EUR)} je Lauf (ChatGPT und Gemini)`;

/** Gruppieren ähnlicher Fan-outs (Schritt 3): Gemini-Embeddings kosten bei ca. 150 kurzen Queries weniger als einen Cent. */
export const groupingCost = "unter 0,01 € (Gemini Embeddings)";

/** Schritt 4: 1 Firecrawl-Credit je gecrawlter Seite, Seiten aus dem 7-Tage-Cache kosten nichts; dazu eine Gemini-Anfrage. */
export const SOURCES_TOKENS = 21000; // gemessen im Live-Lauf S10: 20.879 Tokens, davon der größte Teil Denken des Modells
export function sourcesCost(credits: number): string {
  return `bis zu ${formatCount(credits)} Firecrawl-Credits (1 je Seite, 7 Tage Cache) und ca. ${formatCount(SOURCES_TOKENS)} Tokens Gemini (Cent-Bereich)`;
}

/** Schritt 5: zwei Gemini-Aufrufe (Benennen, Outline) mit Denk-Tokens, Embeddings sind im Cent-Bereich. */
export const TOPICS_TOKENS = 13000; // gemessen im Live-Lauf S11: 12.956 Tokens für Benennen und Outline
export const topicsCost = (tokens: number = TOPICS_TOKENS) =>
  `2 Gemini-Aufrufe (ca. ${formatCount(tokens)} Tokens, Cent-Bereich) und Embeddings (unter 0,01 €)`;
/** „Neu generieren“ der Outline: nur ein Gemini-Aufruf. */
export const outlineCost = `1 Gemini-Aufruf (ca. ${formatCount(Math.round(TOPICS_TOKENS / 2))} Tokens, Cent-Bereich)`;

/** Onpage-Analyse: Verbrauch je URL vom Backend (`estimate_per_url`, im Live-Lauf gemessen, ADR 0020). */
export type OnpageEstimate = Record<string, number>;

export function onpageCost(count: number, per: OnpageEstimate): string {
  const units = formatCount((per.ahrefs_units ?? 0) * count);
  const credits = formatCount((per.firecrawl_credits ?? 0) * count);
  const tokens = formatCount((per.gemini_tokens ?? 0) * count);
  const scope = count === 1 ? "je URL" : `für ${formatCount(count)} URLs`;
  return `ca. ${units} ahrefs-Units, bis zu ${credits} Firecrawl-Credits und ca. ${tokens} Tokens Gemini ${scope} (Abo und Cent-Bereich, keine hohen Euro-Kosten)`;
}

/** Briefing-Abgleich der Onpage-Analyse: ein ahrefs-Abruf für die Positionen der Briefing-Keywords (je 40, mindestens 50 Units). */
export const BRIEFING_CHECK_UNITS = 150;
export const briefingCheckCost = `zusätzlich bis zu ${formatCount(BRIEFING_CHECK_UNITS)} ahrefs-Units für die Positionen der Briefing-Keywords`;

/** Wettbewerber und Content-Gap (ahrefs): Obergrenzen in Units, die das Backend vor dem Start ebenso nennt. */
export function unitsCost(units: number): string {
  return `bis zu ${formatCount(units)} ahrefs-Units (im Abo, keine Euro-Kosten)`;
}
export const unitsCache = "Dieselbe Abfrage kostet innerhalb von 7 Tagen nichts (Cache).";

/**
 * Interne Verlinkung mit Zielseite: Gemini-Embeddings nur für Seitentexte ohne gespeicherten Vektor (ca. 80 Tokens je Seite),
 * die Ähnlichkeit rechnet die Datenbank. Im Free Tier gelten 100 Texte je Minute (Memory „Gemini Free Tier“).
 */
export function linkTargetCost(estimate: { missing: number; tokens: number; minutes: number }, domain: string): string {
  if (estimate.missing === 0) {
    return `keine neuen Embeddings nötig, alle Seiten sind gespeichert; nur Abrufe von ${domain} für Sätze und Anker`;
  }
  const minutes = estimate.minutes === 1 ? "1 Minute" : `${formatCount(estimate.minutes)} Minuten`;
  return `ca. ${formatCount(estimate.tokens)} Tokens Gemini Embeddings für ${formatCount(estimate.missing)} Seitentexte (Cent-Bereich), im Free Tier bis zu ${minutes}; danach nur Abrufe von ${domain}`;
}

/** Hinweis zu ChatGPT und Gemini in der LLM-Analyse: Weboberfläche der kostenlosen Version, das Modell steht an jeder Antwort. */
export const LLM_MODEL_NOTE =
  "ChatGPT und Gemini werden wie in der Weboberfläche der kostenlosen Version abgefragt (DataForSEO LLM Scraper, Land und Sprache des Projekts). Das aktuelle Modell wählt der Anbieter, es steht an jeder Antwort.";
/** Gilt für jede Start-Bestätigung der LLM-Analyse (Formular, Rückfrage, weitere Durchläufe). */
export const LLM_COST_NOTE = "Schätzung ohne Wiederholungen bei Fehlern; abgerechnet wird, was DataForSEO tatsächlich berechnet.";

/** LLM-Analyse: Abfragen über DataForSEO (Euro laut Backend-Schätzung), Markenerkennung in Gemini-Tokens. */
export function llmAnalysisCost(estimate: { total_calls: number; cost_eur: number; extraction_tokens: number }): string {
  const calls = estimate.total_calls === 1 ? "1 Abfrage" : `${formatCount(estimate.total_calls)} Abfragen`;
  return `${calls} über DataForSEO, ca. ${formatEuro(estimate.cost_eur)}; dazu ca. ${formatCount(estimate.extraction_tokens)} Tokens Gemini für die Markenerkennung (Cent-Bereich). ${LLM_COST_NOTE}`;
}
